import '@fontsource/barlow-condensed/600.css';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, MouseEvent } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useToasts from '@/hooks/useToasts';
import {
  SubtitlesTextStyles,
  renderSubtitleImage,
} from '@/pages/MLTranslation/components/SubtitlesStylesCard';
import { SubtitlesGenerationProject } from '@/types/resources';
import { api } from '@/utils/api';
import { SubsGenerationProjectVideoStatus } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';
import srt from '@/utils/srt';

import Card, { ActionButton } from './Card';
import ColorField from './ColorField';
import NumericInput from './NumericInput';

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Box = styled.div`
  padding: 24px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
`;

const Row = styled.div`
  display: flex;
  gap: 24px;
  > div {
    flex: 1;
    max-width: calc(50% - 12px);
  }
`;

const BgRow = styled(Row)`
  padding: 24px 24px 0;
  margin: 24px -24px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
`;

const StyleButton = styled(ActionButton)`
  flex: 1;
  height: 70px;
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 16px;
`;

const ActionCard = styled(Box)`
  padding: 16px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;

const ActionCardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PreviewBox = styled.div`
  position: relative;
  width: 100%;
  padding-top: 288px;
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
`;

const SubtitlesBox = styled.div`
  position: absolute;
  bottom: 0;
  top: 0;
  left: 0;
  right: 0;
  margin: auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface SubtitlesTextProps {
  $bg: string;
  $color: string;
  $opacity: string;
  $fontSize: string;
}

const SubtitlesText = styled.p<SubtitlesTextProps>`
  font-size: 16px;
  padding: 16px;
  line-height: 1.2;
  color: ${({ $color }) => $color};
  font-family: 'Barlow Condensed';
  text-align: center;
  font-size: ${({ $fontSize }) => $fontSize || '32'}px;
  position: relative;
  > span {
    position: relative;
    z-index: 2;
  }
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: ${({ $bg }) => ($bg ? $bg : 'transparent')};
    opacity: ${({ $opacity }) => ($opacity ? parseInt($opacity) / 100 : 1)};
  }
`;

const GenerateButton = styled(ActionButton)`
  width: 100%;
  height: 56px;
  font-weight: 700;
  &:disabled {
    background: ${({ theme }) => theme.colors.lightGray};
  }
`;

const DEFAULT_BACKGROUND_COLOR = '#ffffff';

const DEFAULT_FONT_COLOR = '#000000';

export interface FormDataT {
  styles: {
    font_size: string;
    font_color: string;
    background_color: string;
    background_opacity: string;
    offset: string;
  };
}

interface Props {
  project: SubtitlesGenerationProject;
}

const StylesCard: FC<Props> = ({ project }) => {
  const alerts = useAlerts();
  const toasts = useToasts();
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { isSubmitting, isValid, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      styles: {
        font_size: project.styles?.font_size ?? '36',
        font_color: project.styles?.font_color ?? DEFAULT_FONT_COLOR,
        background_color: project.styles?.background_color ?? '#ffffff',
        background_opacity: project.styles?.background_opacity ?? '80',
        offset: project.styles?.offset ?? '12',
      },
    },
  });

  const submitHandler = handleSubmit(async (data) => {
    const toastId = toasts.create('loading');

    try {
      await api.put(
        `/v1/distribution/subtitles-generation-projects/${project.id}`,
        data,
      );

      queryClient.setQueryData<SubtitlesGenerationProject>(
        [
          'subtitles_generation_projects',
          {
            id: project.id.toString(),
          },
        ],
        (oldData) => ({ ...oldData!, styles: data.styles }),
      );

      toasts.success(toastId, 'Saved');
    } catch (err) {
      handleFormErrors<FormDataT>(err, setError, alerts, true);
      toasts.close(toastId);
    }
  });

  const onSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    clearErrors();
    submitHandler(e);
  };

  const form = watch();

  const backgroundColor = form.styles.background_color;

  const isSubmitDisabled =
    isSubmitting ||
    !isDirty ||
    !isValid ||
    project.videoStatus === SubsGenerationProjectVideoStatus.GENERATING;

  const canGenerateVideo =
    !!project.script &&
    project.videoStatus !== SubsGenerationProjectVideoStatus.GENERATING &&
    project.status !== 'done';

  const onGenerateVideo = async () => {
    try {
      const confirm = await alerts.confirm(
        'Are you sure you want to start this action?',
      );

      if (!confirm) {
        return;
      }

      if (isDirty) {
        await submitHandler();
      }

      const subtitleMaxWidth = `${project.video!.meta!.width - Math.floor(project.video!.meta!.width * 0.3)}px`;

      const subtitles = await Promise.all(
        (project.script ?? []).map(async (part) => {
          return {
            timestamp: part.timestamp,
            image: await renderSubtitleImage(
              part.text,
              '\\n',
              subtitleMaxWidth,
            ),
            position: part.position,
          };
        }),
      );

      const { error, estimated_finish_time } = await api.post<
        never,
        { error: string | null; estimated_finish_time?: string }
      >(
        `/v1/distribution/subtitles-generation-projects/${project.id}/generate`,
        {
          subtitles,
        },
      );

      if (error) {
        return alerts.error(error);
      }

      await queryClient.invalidateQueries(['subtitles_generation_projects']);

      alerts.success(
        'Video generation job has started',
        `Estimated finish time: ${DateTime.fromISO(estimated_finish_time!).toFormat('HH:mm:ss')}`,
      );
    } catch (_) {
      alerts.error();
    }
  };

  const onDownloadSrt = () => {
    const arr = (project.script ?? []).map((row) => {
      const timestamp = row.timestamp
        .split(' - ')
        .map((part, index) => '00:' + part + (!index ? ',001' : ',000'))
        .join(' --> ');

      return {
        ...row,
        timestamp,
      };
    });
    srt.download(arr);
  };

  return (
    <>
      <Card
        storageKey={`subtitles_generation_project_${project.id}_styles_card_visibility`}
        title="Styles and subtitles file"
        defaultIsOpened
        actions={
          <ActionButton onClick={onSubmit} disabled={isSubmitDisabled}>
            Save
          </ActionButton>
        }
        description="Select subtitle styles, and choose to either apply them directly to original video or download subtitles file separately"
        footer={
          <Footer>
            <Text $type="caption">Subtitles</Text>
            <ActionCard>
              <ActionCardContent>
                <Text $type="smallBold">Subtitles File (.srt)</Text>
                <Text $color="gray" $type="smallNormal">
                  Generate and download .srt subtitles file
                </Text>
              </ActionCardContent>
              <ActionButton
                $secondary
                onClick={onDownloadSrt}
                disabled={!canGenerateVideo}
              >
                Download
              </ActionButton>
            </ActionCard>
            <GenerateButton
              onClick={onGenerateVideo}
              disabled={!canGenerateVideo}
            >
              Apply subtitles & generate final video
            </GenerateButton>
          </Footer>
        }
      >
        <Grid>
          <Text $type="caption">Preview Panel</Text>
          <PreviewBox>
            <SubtitlesBox>
              <SubtitlesText
                $bg={form.styles.background_color}
                $opacity={form.styles.background_opacity}
                $color={form.styles.font_color}
                $fontSize={form.styles.font_size}
              >
                <span>
                  Lorem ipsum dolor sit
                  <br />
                  amet consectetur. Nunc.
                </span>
              </SubtitlesText>
            </SubtitlesBox>
          </PreviewBox>
          <Box>
            <ButtonsRow>
              <StyleButton
                $secondary={!!backgroundColor}
                onClick={() =>
                  setValue('styles.background_color', '', { shouldDirty: true })
                }
              >
                Original
              </StyleButton>
              <StyleButton
                $secondary={!backgroundColor}
                onClick={() =>
                  setValue(
                    'styles.background_color',
                    DEFAULT_BACKGROUND_COLOR,
                    { shouldDirty: true },
                  )
                }
              >
                In Background
              </StyleButton>
              <StyleButton disabled>Style 3</StyleButton>
              <StyleButton disabled>Style 4</StyleButton>
            </ButtonsRow>
          </Box>
          <Text $type="caption">Font & Color</Text>
          <Box>
            <Row>
              <ColorField
                control={control}
                name="styles.font_color"
                label="Font Color"
                defaultValue={DEFAULT_FONT_COLOR}
              />
              <NumericInput
                control={control}
                name="styles.font_size"
                label="Font Size"
                inputLabel="px"
              />
            </Row>
            {!!backgroundColor && (
              <BgRow>
                <ColorField
                  control={control}
                  name="styles.background_color"
                  label="Subtitles Background Color"
                  defaultValue={DEFAULT_BACKGROUND_COLOR}
                />
                <NumericInput
                  control={control}
                  name="styles.background_opacity"
                  label="Background Opacity"
                  inputLabel="%"
                />
              </BgRow>
            )}
          </Box>
          <Text $type="caption">Positioning</Text>
          <Box>
            <Row>
              <NumericInput
                control={control}
                name="styles.offset"
                label="Offset"
                inputLabel="%"
              />
            </Row>
          </Box>
        </Grid>
      </Card>
      <SubtitlesTextStyles
        $bg={form.styles.background_color}
        $opacity={form.styles.background_opacity}
        $color={form.styles.font_color}
        $paddingX={'15'}
        $paddingY={'10'}
        $fontSize={form.styles.font_size}
      />
    </>
  );
};

export default StylesCard;
